import { useCallback, type Dispatch } from 'react';
import z from 'zod';
import { makeUseAnalytics } from '../hooks/make-use-analytics.ts';
import { createLocalStorageStore } from '../stores/browser-store.ts';
import { useAppConfig } from './use-app-config.ts';
import { useExternalStore } from './use-external-store.ts';

const validViewModes = ['MANAGER', 'REP'] as const;
export type ViewMode = (typeof validViewModes)[number];

const useAnalytics = makeUseAnalytics<{
    view_mode_switched: {
        view_mode: 'MANAGER' | 'REP';
    };
}>();

const viewModeStore = createLocalStorageStore('plato-view-mode', z.enum(validViewModes));

/**
 * Returns the persisted view mode, defaulting to MANAGER when the manager
 * view is enabled and REP otherwise. The setter emits `view_mode_switched`
 * analytics event when the value changes.
 */
export function useViewMode(): [ViewMode, Dispatch<ViewMode>] {
    const { enableManagerView } = useAppConfig();
    const sendEvent = useAnalytics();
    const [state = 'MANAGER', setState] = useExternalStore(viewModeStore);

    const setStateWithAnalytics = useCallback(
        (next: ViewMode) => {
            setState((prev) => {
                if (prev !== next) {
                    sendEvent('view_mode_switched', { view_mode: next });
                }
                return next;
            });
        },
        [setState, sendEvent],
    );

    // do nothing if not enabled
    if (!enableManagerView) {
        return ['REP', noop];
    }
    return [state, setStateWithAnalytics];
}

/**
 * Returns true if manager view is currently enabled.
 */
export function useIsManagerView(): boolean {
    const [viewMode] = useViewMode();
    return viewMode === 'MANAGER';
}

function noop(): void {}
